"use client";

import { authFetcher } from "@cartz/auth/client";
import { safe } from "@orpc/client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { backendClient } from "@/lib/backend-api";
import { setClientShopId } from "@/lib/client-shop";

export interface OAuthClientApp {
  name?: string | null;
  icon?: string | null;
  origin?: string | null;
}

export interface OAuthShop {
  id: number;
  name: string;
  domain: string;
}

export const useOAuthClient = (clientId: string | null) => {
  return useQuery({
    queryKey: ["oauth-client", clientId],
    enabled: !!clientId,
    queryFn: async (): Promise<OAuthClientApp | null> => {
      if (!clientId) {
        return null;
      }

      const result = await safe(
        backendClient.session.getOAuthClient({ clientId }),
      );
      if (result.error) {
        return null;
      }

      return result.data;
    },
  });
};

export const useOAuthUserShops = () => {
  return useQuery({
    queryKey: ["oauth-shops"],
    queryFn: async (): Promise<{ shops: OAuthShop[] }> => {
      const result = await safe(backendClient.session.listOAuthShops());
      if (result.error) {
        return { shops: [] };
      }

      return result.data;
    },
  });
};

export const useSwitchOAuthShop = () => {
  return useMutation({
    mutationFn: async (shopId: number) => {
      const result = await safe(backendClient.session.update({ shopId }));
      if (result.error) {
        throw new Error("Failed to switch shop");
      }

      setClientShopId(shopId);
      return result.data;
    },
  });
};

export const useSubmitOAuthConsent = () => {
  return useMutation({
    mutationFn: async ({
      accept,
      consentCode,
    }: {
      accept: boolean;
      consentCode: string;
    }) => {
      const data = await authFetcher.oauth2Consent({ accept, consentCode });

      if (!data || data.message) {
        throw new Error(data?.message ?? "Failed to process consent");
      }

      if (!data.redirectURI) {
        throw new Error("Failed to process consent");
      }

      return data as { redirectURI: string };
    },
  });
};
